/**
 * Shared helpers for Home/Dashboard pages
 */

import { useMemo } from "react";

const STATUS_LABELS = { PASS: "pass", NEEDS_FIX: "fail" };

const mapStatus = (st) => STATUS_LABELS[st] ?? "unknown";

const SUMMARY_TEXT = {
  signature: { PASS: "No issues detected", NEEDS_FIX: "Signature needs attention" },
  pii: { PASS: "No PII visible", NEEDS_FIX: "Unredacted PII found" },
};

const buildSummary = (kind, status) => SUMMARY_TEXT[kind][status] ?? "Unable to determine";

const buildValidationIssue = (key, title, section) => ({
  key,
  title,
  status: mapStatus(section.status),
  summary: buildSummary(key, section.status),
  details: Array.isArray(section.what_we_found) ? section.what_we_found : [],
  nextSteps: Array.isArray(section.how_to_fix) ? section.how_to_fix : [],
});

/**
 * Map compliance validation statuses to UI-friendly values
 * @param {string} fileName - Name of the file
 * @param {object} payload - Validation payload with signature_validation and pii_screening
 * @returns {object} Formatted issue object with name, sizeKb, and issues array
 */
export const toIssue = (fileName, payload) => {
  const issues = [];
  const sig = payload?.signature_validation;
  const pii = payload?.pii_screening;

  if (sig) issues.push(buildValidationIssue("signature", "Signature Validation", sig));
  if (pii) issues.push(buildValidationIssue("pii", "PII Screening", pii));

  if (!issues.length) {
    issues.push({
      key: "ok",
      title: "Checks",
      status: "unknown",
      summary: "No structured results returned",
      details: [],
      nextSteps: [],
    });
  }

  return {
    name: fileName,
    sizeKb: null,
    issues,
  };
};

/** Bond retrieval / PG home activity “View Bond” URL (pathname). */
export function getBondUpdateUrl(bondType, bondId) {
  if (!bondId && bondId !== 0) return "";
  switch (bondType) {
    case "VA":
      return `/vermost-va-bond-update/${bondId}`;
    case "Trustee":
      return `/vermost-trustee-bond-update/${bondId}`;
    case "Probate":
      return `/vermost-probate-bond-update/${bondId}`;
    case "Blanket":
      return `/vermost-blanket-bond-update/${bondId}`;
    default:
      return `/vermost-guardianship-bond-update/${bondId}`;
  }
}

/** Resume path for wizard draft cache rows (bond creation flows). */
export function getBondCacheResumePath(bondType, cacheId) {
  if (cacheId == null || cacheId === "") return null;
  switch (bondType) {
    case "Probate":
      return `/vermost-probate-bond-request-cache/${cacheId}`;
    case "Guardianship":
      return `/vermost-guardianship-bond-request-cache/${cacheId}`;
    case "Trustee":
      return `/vermost-trustee-bond-request-cache/${cacheId}`;
    case "VA":
      return `/vermost-va-bond-request-cache/${cacheId}`;
    case "Blanket":
      return `/vermost-blanket-bond-request-cache/${cacheId}`;
    default:
      return null;
  }
}

/** Short relative time label for dashboards (PG / Internal home). */
export function formatRelativeTime(input) {
  if (!input) return "";
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 45) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

/**
 * Handler for deleting a draft bond from cache
 * @param {string} bondCacheId - The bond cache ID to delete
 * @param {function} apiClient - API client function
 * @param {function} setCachedBonds - State setter for cached bonds
 * @param {{ setDeletingId?: (id: string|null) => void }} [options]
 * @returns {Promise<void>}
 */
export const handleDeleteDraft = async (bondCacheId, apiClient, setCachedBonds, options = {}) => {
  if (!bondCacheId) return;

  const confirmed = window.confirm("Delete this draft? This cannot be undone.");
  if (!confirmed) return;

  const { setDeletingId } = options;
  try {
    if (setDeletingId) setDeletingId(bondCacheId);
    await apiClient(`/bond-cache/${bondCacheId}/`, "DELETE");
    setCachedBonds((prev) => prev.filter((r) => r?.bond_cache_id !== bondCacheId));
  } catch (e) {
    console.error("Failed to delete cached bond", e);
  } finally {
    if (setDeletingId) setDeletingId(null);
  }
};

/**
 * Compliance upload dialog flags (Internal / PG home pages).
 * @param {Array} validationResults — list of `{ issues: [{ status, key }] }` from `toIssue`
 */
export function getComplianceValidationSummary(validationResults) {
  const list = Array.isArray(validationResults) ? validationResults : [];
  const hasAnyValidationErrors = list.some((f) =>
    (f.issues || []).some((i) => i.status === "fail")
  );
  const hasBlockingErrors = list.some((f) =>
    (f.issues || []).some((i) => i.status === "fail" && i.key !== "pii")
  );
  const hasPiiWarningsOnly = hasAnyValidationErrors && !hasBlockingErrors;
  return { hasAnyValidationErrors, hasBlockingErrors, hasPiiWarningsOnly };
}

export function useComplianceValidationSummary(validationResults) {
  return useMemo(
    () => getComplianceValidationSummary(validationResults),
    [validationResults]
  );
}
